import type { ReactionEmoji, VideoComment } from '@live-stream/shared'
import { SEEK_MS } from './watch.js'

/**
 * Comments and reactions on the share page (Phase 9), pure: threads in the
 * order they are read, the reactions a playing video passes, the marks on
 * the timeline.
 */

export interface Thread {
  comment: VideoComment
  replies: VideoComment[]
}

/**
 * Comments about a moment first, in the video's order; then the ones about
 * the whole video, oldest first; replies under each, oldest first. A reply
 * whose comment is gone is dropped with it.
 */
export function threads(comments: readonly VideoComment[]): Thread[] {
  const tops = comments.filter((c) => c.parentId === null)
  const byParent = new Map<string, VideoComment[]>()
  for (const c of comments) {
    if (c.parentId === null) continue
    const list = byParent.get(c.parentId) ?? []
    list.push(c)
    byParent.set(c.parentId, list)
  }
  const sorted = [...tops].sort((a, b) => {
    if (a.atMs !== null && b.atMs !== null) return a.atMs - b.atMs || a.createdAt - b.createdAt
    if (a.atMs !== null) return -1
    if (b.atMs !== null) return 1
    return a.createdAt - b.createdAt
  })
  return sorted.map((comment) => ({
    comment,
    replies: (byParent.get(comment.id) ?? []).sort((a, b) => a.createdAt - b.createdAt),
  }))
}

/**
 * The reactions the playhead passed between two looks at it while playing
 * — the ones to float up over the video. A seek passes nothing.
 */
export function passed<T extends { atMs: number }>(
  moments: readonly T[],
  fromMs: number,
  toMs: number,
): T[] {
  // A seek, not playback: nothing floats for what was skipped.
  if (toMs <= fromMs || toMs - fromMs > SEEK_MS) return []
  return moments.filter((m) => m.atMs > fromMs && m.atMs <= toMs)
}

export interface Mark {
  /** 0–1 along the video. */
  at: number
  atMs: number
  count: number
}

/**
 * Timeline marks for comments: one per moment, merged when they would sit
 * closer than `minGap` of the bar, so a busy minute reads as one mark with
 * a count rather than a smear.
 */
export function marks(times: readonly number[], durationMs: number, minGap = 0.012): Mark[] {
  if (durationMs <= 0) return []
  const sorted = times
    .filter((t) => t >= 0 && t <= durationMs)
    .slice()
    .sort((a, b) => a - b)
  const out: Mark[] = []
  for (const t of sorted) {
    const at = t / durationMs
    const last = out[out.length - 1]
    if (last && at - last.at < minGap) last.count += 1
    else out.push({ at, atMs: t, count: 1 })
  }
  return out
}

/** How many reactions of each kind, for the buttons. */
export const countOf = (
  counts: Partial<Record<ReactionEmoji, number>>,
  emoji: ReactionEmoji,
): number => counts[emoji] ?? 0

/** "just now", "5 min ago", "3 h ago", "2 d ago", then the date. */
export function ago(ms: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - ms) / 1000))
  if (s < 45) return 'just now'
  const m = Math.round(s / 60)
  if (m < 60) return `${m} min ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h} h ago`
  const d = Math.round(h / 24)
  if (d < 7) return `${d} d ago`
  return new Date(ms).toLocaleDateString([], { dateStyle: 'medium' })
}
